/**
 * EMBER & PALM — compact editorial navigation, dominant booking action, restrained order menu,
 * warm parchment footer, and mobile BOOK / MENU / CALL rail. Avoid generic cards and excessive ornament.
 */
import { type MouseEvent, useState } from "react";
import { Link, useLocation } from "wouter";
import { CalendarDays, ChevronDown, ExternalLink, Menu as MenuIcon, Phone, ShoppingBag, ShoppingCart, Utensils, X } from "lucide-react";
import { ShopCart } from "@/components/ShopCart";
import { useCart } from "@/contexts/CartContext";
import { AMOK_PASTE } from "@/lib/amokPaste";
import { SITE, trackEvent } from "@/lib/site";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const nav = [
  ["Home", "/"],
  ["Menu", "/menu"],
  ["Lunch", "/lunch"],
  ["Shop", AMOK_PASTE.productPath],
  ["Our Story", "/our-story"],
  ["Functions & Groups", "/groups-functions"],
  ["Contact & Hours", "/location"],
];

export function SiteLayout({ children }: { children: React.ReactNode }) {
  const [open, setOpen] = useState(false);
  const [location] = useLocation();
  const { isOpen: isCartOpen, itemCount, openCart } = useCart();
  const cartCount = itemCount > 99 ? "99+" : String(itemCount);
  const cartLabel = itemCount === 0 ? "Shop, cart empty" : `Open cart, ${itemCount} ${itemCount === 1 ? "item" : "items"}`;

  const handleShopClick = (event: MouseEvent<HTMLAnchorElement>, placement: "desktop" | "mobile") => {
    if (placement === "mobile") setOpen(false);
    if (itemCount === 0) return;
    event.preventDefault();
    openCart();
    trackEvent("shop_cart_open", { item_count: String(itemCount), placement });
  };

  return (
    <div className={`site-shell${open ? " menu-open" : ""}`}>
      <a className="skip-link" href="#main-content">Skip to main content</a>
      <header className="site-header">
        <div className="container header-inner">
          <Link href="/" className="brand-lockup" aria-label="ANGKOR Cambodian Flavours home">
            <img src={SITE.images.markCompact} alt="" width="52" height="52" />
            <span><strong>ANGKOR</strong><small>Cambodian Flavours</small></span>
          </Link>

          <nav className="desktop-nav" aria-label="Primary navigation">
            {nav.map(([label, href]) => (
              <Link
                className={`${location === href ? "active " : ""}${href === AMOK_PASTE.productPath ? "shop-nav" : ""}`.trim()}
                href={href}
                key={href}
                aria-current={location === href ? "page" : undefined}
                aria-label={href === AMOK_PASTE.productPath ? cartLabel : undefined}
                aria-haspopup={href === AMOK_PASTE.productPath && itemCount > 0 ? "dialog" : undefined}
                aria-expanded={href === AMOK_PASTE.productPath && itemCount > 0 ? isCartOpen : undefined}
                aria-controls={href === AMOK_PASTE.productPath && itemCount > 0 ? "shop-cart-drawer" : undefined}
                onClick={href === AMOK_PASTE.productPath ? event => handleShopClick(event, "desktop") : undefined}
              >
                {href === AMOK_PASTE.productPath && <><ShoppingCart size={14} aria-hidden="true" /><span className="shop-cart-count" aria-hidden="true">{cartCount}</span></>}
                {label}
              </Link>
            ))}
          </nav>
          <span className="sr-only" role="status" aria-live="polite">
            {itemCount === 0 ? "Cart is empty" : `Cart now contains ${itemCount} ${itemCount === 1 ? "item" : "items"}`}
          </span>

          <div className="header-actions">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="button order-trigger" type="button" aria-label="Choose food ordering or buy gift cards online">
                  Order Food &amp; Gift Cards <ChevronDown size={15} aria-hidden="true" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="order-dropdown" align="end" sideOffset={10}>
                <DropdownMenuLabel className="order-dropdown-label">Food ordering &amp; gift cards</DropdownMenuLabel>
                <DropdownMenuItem asChild className="order-dropdown-item">
                  <a href={SITE.directOrder} target="_blank" rel="noreferrer" aria-label="Order Direct & Gift Cards (opens in a new tab)" onClick={() => trackEvent("direct_order_click", { placement: "header_order_menu" })}>
                    <ShoppingBag aria-hidden="true" />
                    <span><strong>Order Direct &amp; Gift Cards</strong><small>Order food or buy gift cards online</small></span>
                    <ExternalLink aria-hidden="true" />
                  </a>
                </DropdownMenuItem>
                <DropdownMenuItem asChild className="order-dropdown-item">
                  <a href={SITE.uberEats} target="_blank" rel="noreferrer" aria-label="Uber Eats (opens in a new tab)" onClick={() => trackEvent("uber_eats_click", { placement: "header_order_menu" })}>
                    <Utensils aria-hidden="true" />
                    <span><strong>Uber Eats</strong><small>Delivery through Uber Eats</small></span>
                    <ExternalLink aria-hidden="true" />
                  </a>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            <a
              className="button button-gold header-book"
              href={SITE.booking}
              onClick={() => trackEvent("book_table_click", { placement: "header" })}
            >
              Book a Table
            </a>
          </div>

          <button type="button" className="mobile-menu-button" onClick={() => setOpen(!open)} aria-label="Toggle menu" aria-expanded={open} aria-controls="mobile-navigation">
            {open ? <X /> : <MenuIcon />}
          </button>
        </div>

        {open && (
          <nav className="mobile-menu" id="mobile-navigation" aria-label="Mobile navigation">
            {nav.map(([label, href]) => (
              <Link
                className={href === AMOK_PASTE.productPath ? "shop-nav" : undefined}
                href={href}
                key={href}
                aria-current={location === href ? "page" : undefined}
                aria-label={href === AMOK_PASTE.productPath ? cartLabel : undefined}
                aria-haspopup={href === AMOK_PASTE.productPath && itemCount > 0 ? "dialog" : undefined}
                aria-expanded={href === AMOK_PASTE.productPath && itemCount > 0 ? isCartOpen : undefined}
                aria-controls={href === AMOK_PASTE.productPath && itemCount > 0 ? "shop-cart-drawer" : undefined}
                onClick={href === AMOK_PASTE.productPath ? event => handleShopClick(event, "mobile") : () => setOpen(false)}
              >
                {href === AMOK_PASTE.productPath && <><ShoppingCart size={17} aria-hidden="true" /><span className="shop-cart-count" aria-hidden="true">{cartCount}</span></>}
                {label}
              </Link>
            ))}
            <Link href="/wholesale" onClick={() => setOpen(false)}>Wholesale Interest</Link>
            <div className="mobile-order-block">
              <span>Order Food &amp; Gift Cards</span>
              <a href={SITE.directOrder} target="_blank" rel="noreferrer" aria-label="Order Direct & Gift Cards (opens in a new tab)" onClick={() => { setOpen(false); trackEvent("direct_order_click", { placement: "mobile_menu" }); }}>Order Direct &amp; Gift Cards <ExternalLink aria-hidden="true" /></a>
              <a href={SITE.uberEats} target="_blank" rel="noreferrer" aria-label="Uber Eats (opens in a new tab)" onClick={() => { setOpen(false); trackEvent("uber_eats_click", { placement: "mobile_menu" }); }}>Uber Eats <ExternalLink aria-hidden="true" /></a>
            </div>
            <a className="mobile-menu-book" href={SITE.booking} onClick={() => { setOpen(false); trackEvent("book_table_click", { placement: "mobile_menu" }); }}>Book a Table</a>
          </nav>
        )}
      </header>

      <main id="main-content" tabIndex={-1}>{children}</main>

      <footer className="site-footer">
        <div className="container footer-grid">
          <div className="footer-brand">
            <img src={SITE.images.markCompact} alt="" width="64" height="64" loading="lazy" />
            <h2>ANGKOR <span>Cambodian Flavours</span></h2>
            <p>Family recipes, fragrant kroeung and warm Cambodian hospitality in West Melbourne.</p>
            <p className="footer-business">Angkor Cafe Pty Ltd · ABN 76 639 824 874</p>
          </div>
          <div>
            <p className="footer-heading">Visit</p>
            <address>{SITE.address}</address>
            <a href={`tel:${SITE.phone}`}>{SITE.phoneDisplay}</a>
            <a href={`mailto:${SITE.email}`}>{SITE.email}</a>
          </div>
          <div>
            <p className="footer-heading">Explore</p>
            <Link href="/menu">Dinner Menu</Link>
            <Link href="/lunch">Lunch</Link>
            <Link href="/our-story">Our Story</Link>
            <Link href="/groups-functions">Functions & Groups</Link>
            <Link href="/fish-amok-melbourne">Fish Amok in Melbourne</Link>
            <Link href="/cambodian-restaurant-melbourne-cbd">Cambodian restaurant near the CBD</Link>
            <Link href="/restaurant-near-marvel-stadium-southern-cross">Dining near Marvel Stadium & Southern Cross</Link>
            <Link href={AMOK_PASTE.productPath}>Amok Curry Paste</Link>
            <Link href="/recipes">Cambodian Food Guide</Link>
            <Link href="/wholesale">Wholesale Interest</Link>
            <Link href="/blog">Blog</Link>
          </div>
          <div>
            <p className="footer-heading">Opening Hours</p>
            <p>Tue–Fri lunch & dinner</p>
            <p>Sat lunch & dinner · Sun dinner</p>
            <Link href="/location">Contact & Hours</Link>
            <div className="social-row">
              <a href={SITE.instagram} target="_blank" rel="noreferrer" aria-label="Instagram (opens in a new tab)">Instagram</a>
              <a href={SITE.facebook} target="_blank" rel="noreferrer" aria-label="Facebook (opens in a new tab)">Facebook</a>
              <a href={SITE.tiktok} target="_blank" rel="noreferrer" aria-label="TikTok (opens in a new tab)">TikTok</a>
            </div>
          </div>
        </div>
        <div className="container footer-bottom">
          <span>© 2026 ANGKOR Cambodian Flavours</span>
          <Link href="/privacy">Privacy</Link>
        </div>
      </footer>
      <ShopCart />

      <nav className="mobile-cta" aria-label="Quick actions">
        <a href={SITE.booking} onClick={() => trackEvent("book_table_click", { placement: "mobile_sticky" })}>
          <CalendarDays aria-hidden="true" /> <span>Book</span>
        </a>
        <Link href="/menu" onClick={() => trackEvent("menu_view", { placement: "mobile_sticky" })}>
          <Utensils aria-hidden="true" /> <span>Menu</span>
        </Link>
        <a href={`tel:${SITE.phone}`} onClick={() => trackEvent("phone_click", { placement: "mobile_sticky" })}>
          <Phone aria-hidden="true" /> <span>Call</span>
        </a>
      </nav>
    </div>
  );
}
